import React, { Fragment, useRef, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { useDrag, useDrop } from 'react-dnd'
import { useSlate, useReadOnly, ReactEditor } from 'vendor/slate-react'
import { Node, Transforms, Path } from 'vendor/slate'
import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { EditorDispatch } from 'cg/components/documents/editor/slate/reducer'

import { insertTab } from 'cg/components/documents/editor/slate/plugins/tabs'

const TabsHeaderItem = ({ attributes, children, element, editor }) => {
  const { state, dispatch } = useContext(EditorDispatch) || {}
  const { activeTabs } = state || {}
  const readOnly = useReadOnly()
  const { tabId } = element
  const path = ReactEditor.findPath(editor, element)
  const tabsHeaderPath = (() => {
    try {
      return Node.has(editor, path) ? Path.parent(path) : null
    } catch (error) {
      return null
    }
  })()
  const tabsPath = (() => {
    try {
      return tabsHeaderPath && Node.has(editor, tabsHeaderPath) ? Path.parent(tabsHeaderPath) : null
    } catch (error) {
      return null
    }
  })()
  const tabs = tabsPath && Node.has(editor, tabsPath) ? Node.get(editor, tabsPath) : null

  const dropRef = useRef(null)

  useEffect(() => {
    if (
      tabs &&
      tabs.children &&
      tabs.children[0] &&
      tabs.children[0].children &&
      tabs.children[0].children[0] &&
      tabs.defaultActiveTab !== tabs.children[0].children[0].tabId
    ) {
      Transforms.setNodes(editor, { defaultActiveTab: tabs.children[0].children[0].tabId }, { at: tabsPath })
    }
  }, [tabs, tabsPath, editor])

  // Remove orphan tab headers after undo (defer so paste/normalize can finish first).
  useEffect(() => {
    if (tabs && tabsPath) return undefined

    const frame = requestAnimationFrame(() => {
      if (!Node.has(editor, path)) return

      try {
        const headerPath = ReactEditor.findPath(editor, element)
        const parentTabsPath = Path.parent(Path.parent(headerPath))
        if (Node.has(editor, parentTabsPath) && Node.get(editor, parentTabsPath).type === ELEMENT_TYPES.tabs) {
          return
        }
      } catch (error) {
        // Header is detached from a tabs block.
      }

      if (Node.has(editor, path)) {
        Transforms.removeNodes(editor, { at: path })
      }
    })

    return () => cancelAnimationFrame(frame)
  }, [tabs, tabsPath, path, editor, element])

  const onClick = tabId => {
    if (tabs) {
      activeTabs[tabs.id] = tabId
      dispatch({ type: 'activateTab', payload: activeTabs })
    }
  }

  const deleteTab = tabId => {
    const tabsHeader = tabsHeaderPath ? Node.get(editor, tabsHeaderPath) : null
    const tabIndex = tabsHeader.children.findIndex(i => i.tabId === tabId)

    if (!tabsHeader) return

    if (tabsHeader.children.length === 1) {
      // Remove entire tab block if only one tab left
      if (tabsPath) {
        Transforms.removeNodes(editor, { at: tabsPath })
      }
    } else {
      const tabContent = tabs.children.find(tab => tab.tabId === tabId && tab.type === ELEMENT_TYPES.tabs_content_item)
      let newTabId = tabId

      if (tabIndex === 0) {
        // if index is 0 select the next tab
        newTabId = tabsHeader.children[tabIndex + 1].tabId
      } else {
        // select the previous tab
        newTabId = tabsHeader.children[tabIndex - 1].tabId
      }

      onClick(newTabId)

      Transforms.removeNodes(editor, { at: path })
      Transforms.removeNodes(editor, { at: ReactEditor.findPath(editor, tabContent) })
    }
  }

  const insertTabItem = () => {
    const tabContent = tabs.children.find(tab => tab.tabId === tabId && tab.type === ELEMENT_TYPES.tabs_content_item)
    insertTab(editor, path, ReactEditor.findPath(editor, tabContent))
  }

  const [{ isOver }, drop] = useDrop({
    accept: 'TabHeaderItem',
    collect: monitor => ({
      isOver: monitor.isOver()
    }),
    drop(item) {
      const atPath = ReactEditor.findPath(editor, item)
      const toPath = ReactEditor.findPath(editor, element)

      // Don't replace items with themselves
      if (!atPath || !toPath || JSON.stringify(atPath) === JSON.stringify(toPath) || atPath.length !== toPath.length) {
        return
      }

      Transforms.moveNodes(editor, {
        at: atPath,
        to: toPath
      })
    }
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'TabHeaderItem',
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    item() {
      return element
    },
    canDrag() {
      return !readOnly
    }
  })

  drop(dropRef)

  const isTabActive = activeTabs ? (tabs ? activeTabs[tabs.id] === tabId : false) : tabs ? tabs.defaultActiveTab === tabId : false

  return (
    <Tab ref={dropRef} active={isTabActive}>
      <>
        {!readOnly && (
          <TabActionsWrapper contentEditable={false} style={{ userSelect: 'none' }}>
            <TabActionsButton
              isDragging={isDragging}
              onClick={() => {
                deleteTab(tabId)
              }}
            >
              <i className="fad fa-trash" />
            </TabActionsButton>
          </TabActionsWrapper>
        )}
        <TabTitleContainer
          active={isTabActive}
          ref={preview}
          isOver={isOver}
          onClick={() => {
            onClick(tabId)
          }}
        >
          {!readOnly && (
            <TabDragHandle ref={drag} contentEditable={false} style={{ userSelect: 'none' }} active={isTabActive}>
              <i className="fa fa-grip-vertical" />
            </TabDragHandle>
          )}
          <TabTitle {...attributes} active={isTabActive} readOnly={readOnly}>
            {children}
          </TabTitle>
          {!readOnly && (
            <TabAddItem
              onClick={() => {
                insertTabItem()
              }}
              contentEditable={false}
              style={{ userSelect: 'none' }}
            >
              <i className="far fa-plus" />
            </TabAddItem>
          )}
        </TabTitleContainer>
      </>
    </Tab>
  )
}

export default TabsHeaderItem

const TabAddItem = styled.div`
  width: 20px;
  height: 20px;
  font-size: 13px;
  border-radius: 4px;
  position: absolute;
  cursor: pointer;
  right: -10px;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.bgLightest};
  border: 1px solid ${props => props.theme.borderColor};
  color: ${props => props.theme.textGreyTinted};
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.05);
  -webkit-transition: 0.15s;
  transition: 0.15s;
  display: none;
  z-index: 2;
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`

const TabActionsWrapper = styled.div`
  display: flex;
  width: 100%;
  background: transparent;
`

const TabDragHandle = styled.div`
  cursor: grab;
  display: ${props => (props.active ? 'flex' : 'none')};
  margin: 0px;
  min-width:30px;
  height:100%;
  justify-content:center;
  align-items:center;
  z-index: 2;
  color: ${props => props.theme.textGreyTintedLighter};
  i {
    font-size: 12px;
  }
}
`

const TabActionsButton = styled.div`
  background: ${props => props.theme.bgLightestInverse};
  font-size: 13.5px;
  height: 21px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0px 2px 5px 2px;
  color: ${props => props.theme.textGreyTintedLighter};
  border-radius: 3px;
  padding: 4px;
  width: 100%;
  display: flex;
  -webkit-transition: 0.15s;
  visibility: hidden;
  &.is-dragging {
    visibility: hidden !important;
  }
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`

const Tab = styled.li`
  flex: 1;
  max-width: 90%;
  font-size: 12.75px;
  display: flex;
  color: ${props => props.theme.textGreyTintedLighter};
  margin: 0px;
  font-weight: bold;
  overflow: ${props => (props.active ? 'visible' : 'hidden')};
  border-bottom: ${props => (props.active ? '1px solid transparent' : `1px solid ${props.theme.borderColor}`)};
  color: ${props => (props.active ? props.theme.textGreyTintedDarker : props.theme.textGreyTintedLighter)};
  min-width: ${props => (props.active ? '0px' : 'auto')};
  z-index: ${props => (props.active ? '1' : 'auto')};
  margin-bottom: ${props => (props.active ? '-1px' : '0px')};
  background: transparent;
  flex-direction: column;
  cursor: default;
  &:hover {
    color: ${props => props.theme.textGreyTintedDarker};
    ${TabActionsButton} {
      visibility: ${props => (props.active ? 'visible' : 'hidden')};
    }
    ${TabAddItem} {
      display: ${props => (props.active ? 'flex' : 'none')};
    }
  }

  &:last-child {
    > span {
      border-top-right-radius: 3px;
    }
  }
  &:first-child {
    > span {
      border-left: 1px solid ${props => props.theme.borderColor};
      border-top-left-radius: 3px;
    }
  }
  &:only-child {
    max-width: 100%;
  }
`

const TabTitleContainer = styled.span`
  overflow: ${props => (props.active ? 'visible' : 'hidden')};
  padding: ${props => (props.active ? '0px' : '10px 0px')};
  height: ${props => (props.active ? '40px' : '39px')};
  background: ${props =>
    props.isOver ? props.theme.hoverBg : props.active ? props.theme.bgLightest : props.theme.bgSecondLightest};
  border-right: 1px solid ${props => props.theme.borderColor};
  border-top: 1px solid ${props => props.theme.borderColor};
  width: 100%;
  text-overflow: ellipsis;
  word-break: normal;
  white-space: pre;
  display: flex;
  align-items: center;
  position: relative;
}
`

const TabTitle = styled.div`
  padding: ${props => (props.active ? (props.readOnly ? '0px 15px 0px 15px' : '0px 15px 0px 0px') : '0px 15px')};
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
`
